import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Progress, Spin } from 'antd';
import styled from 'styled-components';

import { SkillIcon } from 'atoms';
import { Colors } from 'context';
import { useSkillDetail } from 'core';
import { PATHS } from 'router/config';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';

/**
 * Страница одного навыка (ТЗ 0039, доводка UX).
 *
 * 🔴 Заменяет выдвижную шторку карты навыков. Шторку нельзя ни дать ссылкой,
 * ни открыть с другого экрана, ни вернуть кнопкой «назад» — а именно это и
 * нужно, когда навык упоминается в профессии, в вакансии и в резюме сразу.
 * Владелец: «кликаешь на умение, открывается справа поп-ап — не очевидно».
 *
 * Порядок блоков и есть ответ на «зачем эта страница»:
 *   1. что это за навык;
 *   2. есть ли он у меня и откуда;
 *   3. нужен ли он рынку — по НАШЕЙ ленте, и это подписано;
 *   4. КУДА он ведёт: в какие профессии и какими нашими курсами берётся.
 * Четвёртый блок — та самая связь, которой не хватало: из навыка видно цель.
 */
/** Сколько курсов показывать сразу. Остальные — под раскрытием. */
const TOP_PROVIDERS = 1;

export const GrowthSkill = memo(() => {
  const { t } = useTranslation();
  const { slug = '' } = useParams<{ slug: string }>();
  const { skill, loading } = useSkillDetail(slug);

  if (loading && !skill) {
    return (
      <PageWrapper>
        <Center>
          <Spin size="large" />
        </Center>
      </PageWrapper>
    );
  }

  if (!skill) {
    return (
      <PageWrapper>
        <BackLink to={PATHS.careerSkills}>{t('growth.skill.back')}</BackLink>
        <Muted>{t('growth.skill.notFound')}</Muted>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BackLink to={PATHS.careerSkills}>{t('growth.skill.back')}</BackLink>

      <Head>
        <SkillIcon
          slug={skill.slug}
          name={skill.name}
          category={skill.category}
          iconUrl={skill.iconUrl}
          size={56}
        />
        <HeadText>
          <Title>{skill.name}</Title>
          <Category>
            {t(`growth.map.category_${skill.category}`, {
              defaultValue: skill.category,
            })}
          </Category>
        </HeadText>
      </Head>

      {/* Пустое описание не заменяем выдуманным: справочник заполняет
          методист, и заглушка была бы обещанием, за которое здесь никто не
          отвечает. */}
      {skill.description ? <Lead>{skill.description}</Lead> : null}

      <Columns>
        <Block>
          <BlockTitle>{t('growth.skill.mine')}</BlockTitle>
          {skill.myLevel ? (
            <>
              <Progress
                percent={(skill.myLevel / 5) * 100}
                showInfo={false}
                strokeColor={Colors.signal_green}
              />
              <Line>{t('growth.map.level', { n: skill.myLevel })}</Line>
              <Muted>
                {skill.mySource === 'course'
                  ? skill.grantedLessonNumber
                    ? t('growth.map.detailsFromLesson', {
                        n: skill.grantedLessonNumber,
                        course: skill.grantedCourseName || '',
                      })
                    : t('growth.map.detailsFromSchool', {
                        course: skill.grantedCourseName || '',
                      })
                  : t('growth.map.detailsSelfClaimed')}
              </Muted>
            </>
          ) : (
            <Muted>{t('growth.skill.notMine')}</Muted>
          )}
        </Block>

        <Block>
          <BlockTitle>{t('growth.skill.demand')}</BlockTitle>
          {skill.liveVacancies === null ? (
            <Muted>{t('growth.skill.demandUnknown')}</Muted>
          ) : (
            <>
              <Bars>
                {[1, 2, 3].map((step) => (
                  <Bar key={step} filled={skill.demandBand >= step} />
                ))}
              </Bars>
              <Line>
                {skill.liveVacancies
                  ? t('growth.map.demandCount', { n: skill.liveVacancies })
                  : t('growth.map.demandNone')}
              </Line>
              {/* Оговорка обязательна: это НАША доска, а не рынок труда. */}
              <Muted>{t('growth.map.detailsDemandNote')}</Muted>
            </>
          )}
        </Block>
      </Columns>

      <Block>
        <BlockTitle>
          {skill.myLevel ? t('growth.map.detailsDeepen') : t('growth.map.whereToGet')}
        </BlockTitle>
        {/* ТЗ 0069: наверху ОДИН лучший курс, остальные — под раскрытием.
            Аудит владельца: тринадцать курсов подряд дают парадокс выбора —
            человек не выбирает ни одного. Порядок задаёт сервер (сначала тот,
            что даёт навык глубже), поэтому «лучший» — это просто первый, а не
            вторая сортировка на клиенте. */}
        {skill.providers.length ? (
          <>
            {skill.providers.slice(0, TOP_PROVIDERS).map((provider) => (
              <Row key={provider.courseId}>
                <CourseLink href={provider.courseUrl} target="_blank" rel="noopener noreferrer">
                  {provider.courseName}
                </CourseLink>
                {provider.completed ? <Muted> {t('growth.map.courseDone')}</Muted> : null}
              </Row>
            ))}
            {skill.providers.length > TOP_PROVIDERS ? (
              <More>
                <MoreSummary>
                  {t('growth.map.moreCourses', {
                    n: skill.providers.length - TOP_PROVIDERS,
                  })}
                </MoreSummary>
                {skill.providers.slice(TOP_PROVIDERS).map((provider) => (
                  <Row key={provider.courseId}>
                    <CourseLink href={provider.courseUrl} target="_blank" rel="noopener noreferrer">
                      {provider.courseName}
                    </CourseLink>
                    {provider.completed ? <Muted> {t('growth.map.courseDone')}</Muted> : null}
                  </Row>
                ))}
              </More>
            ) : null}
          </>
        ) : (
          <Muted>{t('growth.map.noCourseYet')}</Muted>
        )}
      </Block>

      {/* 🔴 Ради этого блока страница и заводилась: из навыка видно, КУДА он
          ведёт. Владелец жаловался, что связи между профессией, навыками и
          курсами не видно вовсе. */}
      <Block>
        <BlockTitle>{t('growth.skill.professions')}</BlockTitle>
        <Muted>{t('growth.skill.professionsHint')}</Muted>
        <Cards>
          {skill.professions.map((row) => (
            <ProfessionCard key={row.code} to={PATHS.careerProfession.replace(':code', row.code)}>
              <CardTitle>{row.title}</CardTitle>
              {row.summary ? <CardText>{row.summary}</CardText> : null}
              <Muted>
                {t('growth.skill.professionProgress', {
                  have: row.haveCount,
                  total: row.totalCount,
                })}
              </Muted>
            </ProfessionCard>
          ))}
        </Cards>
      </Block>
    </PageWrapper>
  );
});

/* 🔴 width: 100% обязателен — шаблон страницы это flex-СТРОКА, и без него
   обёртка сжимается по содержимому (шрам записан в Growth.tsx). */
const PageWrapper = styled.div`
  width: 100%;
  padding: 32px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${desktop} {
    padding: 54px 0 104px;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const BackLink = styled(Link)`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeadText = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  ${fontService.h3()}

  ${tablet} {
    ${fontService.h2()}
  }
`;

const Category = styled.div`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Lead = styled.p`
  margin: 0;
  max-width: 720px;
  ${fontService.text({ type: TextType.medium })}
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Block = styled.section`
  padding: 16px 18px;
  border: 1px solid ${Colors.gray};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BlockTitle = styled.h2`
  margin: 0;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Line = styled.div`
  ${fontService.text({ type: TextType.small })}
`;

const Muted = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Row = styled.div`
  margin-bottom: 4px;
`;

const CourseLink = styled.a`
  /* Подчёркнутая ссылка обычным цветом. main_dark это #F0C015 в светлой
     теме — 1.71:1 на белом; тот же класс дефекта, что и чистый жёлтый
     (правило в context/constants/colors.ts). */
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Bars = styled.div`
  display: flex;
  gap: 4px;
`;

const Bar = styled.span<{ filled: boolean }>`
  width: 28px;
  height: 6px;
  border-radius: 3px;
  background: ${({ filled }) => (filled ? Colors.signal_green : Colors.gray)};
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 8px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const ProfessionCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid ${Colors.gray};
  border-radius: 10px;
  color: inherit;

  &:hover {
    border-color: ${Colors.main};
  }
`;

const CardTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const CardText = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const More = styled.details`
  margin-top: 8px;
`;

const MoreSummary = styled.summary`
  cursor: pointer;
  margin-bottom: 6px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;
